import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLeads } from '../api/leadService';
import { normalizeLead } from '../utils/leadModel';

function getHealth(account) {
  if (account.convertedCount > 0) return 'Healthy';
  if (account.highRiskCount > 0) return 'At Risk';
  return 'Needs Attention';
}

function buildAccounts(leads) {
  const map = new Map();

  leads.forEach((lead) => {
    const company = (lead.companyName || '').trim() || 'Unknown Company';
    if (!map.has(company)) {
      map.set(company, {
        company,
        leads: [],
        convertedCount: 0,
        inProgressCount: 0,
        highPriorityCount: 0,
        highRiskCount: 0,
        unassignedCount: 0,
        latestUpdateAt: null,
      });
    }

    const entry = map.get(company);
    entry.leads.push(lead);

    if (lead.status === 'Converted') entry.convertedCount += 1;
    if (['Contacted', 'Qualified', 'Nurturing'].includes(lead.status)) entry.inProgressCount += 1;
    if (['High', 'Urgent'].includes(lead.priority)) entry.highPriorityCount += 1;
    if (['Lost', 'Rejected'].includes(lead.status)) entry.highRiskCount += 1;
    if (!lead.assignedTo) entry.unassignedCount += 1;

    if (lead.updatedAt) {
      const current = new Date(lead.updatedAt).getTime();
      const prev = entry.latestUpdateAt ? new Date(entry.latestUpdateAt).getTime() : 0;
      if (!prev || current > prev) entry.latestUpdateAt = lead.updatedAt;
    }
  });

  return Array.from(map.values())
    .map((entry) => ({
      ...entry,
      leadCount: entry.leads.length,
      health: getHealth(entry),
      latestUpdateLabel: entry.latestUpdateAt
        ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(entry.latestUpdateAt))
        : '—',
    }))
    .sort((a, b) => b.leadCount - a.leadCount);
}

function AccountsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState('');
  const [activeAccount, setActiveAccount] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadAllLeads() {
      setLoading(true);
      setError('');
      try {
        let page = 1;
        const combined = [];
        const pageLimit = 100;

        while (page <= 30) {
          const res = await fetchLeads({ page, limit: pageLimit });
          const rows = Array.isArray(res.data) ? res.data : [];
          combined.push(...rows.map(normalizeLead));

          if (!res.pagination?.has_next_page) break;
          page += 1;
        }

        if (mounted) setLeads(combined);
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || 'Unable to load accounts.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAllLeads();
    return () => {
      mounted = false;
    };
  }, []);

  const accounts = useMemo(() => buildAccounts(leads), [leads]);

  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const matchSearch = !search || account.company.toLowerCase().includes(search.toLowerCase());
      const matchHealth = !healthFilter || account.health === healthFilter;
      return matchSearch && matchHealth;
    });
  }, [accounts, search, healthFilter]);

  const summary = useMemo(() => {
    const total = accounts.length;
    const healthy = accounts.filter((a) => a.health === 'Healthy').length;
    const atRisk = accounts.filter((a) => a.health === 'At Risk').length;
    const needsAttention = accounts.filter((a) => a.health === 'Needs Attention').length;
    return { total, healthy, atRisk, needsAttention };
  }, [accounts]);

  return (
    <div className="dashboard-page">
      <div className="page-title-row">
        <h2>Accounts</h2>
      </div>

      <section className="dashboard-kpis accounts-kpis">
        <article className="dashboard-kpi-card accounts-kpi-card accounts-kpi-card--total">
          <span>Total Accounts</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="dashboard-kpi-card accounts-kpi-card accounts-kpi-card--healthy">
          <span>Healthy Accounts</span>
          <strong>{summary.healthy}</strong>
        </article>
        <article className="dashboard-kpi-card accounts-kpi-card accounts-kpi-card--risk">
          <span>At Risk</span>
          <strong>{summary.atRisk}</strong>
        </article>
        <article className="dashboard-kpi-card accounts-kpi-card accounts-kpi-card--attention">
          <span>Needs Attention</span>
          <strong>{summary.needsAttention}</strong>
        </article>
      </section>

      <div className="filter-bar">
        <input
          className="filter-input"
          placeholder="Search by company name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="filter-select"
          value={healthFilter}
          onChange={(e) => setHealthFilter(e.target.value)}
        >
          <option value="">All Health States</option>
          <option value="Healthy">Healthy</option>
          <option value="At Risk">At Risk</option>
          <option value="Needs Attention">Needs Attention</option>
        </select>
      </div>

      {loading ? <p>Loading accounts...</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      {!loading && !error && (
        <div className="dashboard-panel dashboard-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Total Leads</th>
                <th>Converted</th>
                <th>In Progress</th>
                <th>High Priority</th>
                <th>Unassigned</th>
                <th>Last Update</th>
                <th>Health</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length ? (
                filteredAccounts.map((account) => (
                  <tr key={account.company}>
                    <td>
                      <button
                        type="button"
                        className="lead-name-btn"
                        onClick={() => setActiveAccount(account)}
                      >
                        {account.company}
                      </button>
                    </td>
                    <td>{account.leadCount}</td>
                    <td>{account.convertedCount}</td>
                    <td>{account.inProgressCount}</td>
                    <td>{account.highPriorityCount}</td>
                    <td>{account.unassignedCount}</td>
                    <td>{account.latestUpdateLabel}</td>
                    <td>
                      <span className={`account-health-pill account-health-pill--${account.health.toLowerCase().replace(' ', '-')}`}>
                        {account.health}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="table-action-link" onClick={() => setActiveAccount(account)}>
                        View Leads
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="9">No accounts found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeAccount && (
        <>
          <div className="drawer-backdrop" onClick={() => setActiveAccount(null)} />
          <aside className="lead-drawer">
            <div className="drawer-header">
              <h3>{activeAccount.company}</h3>
              <button className="modal-close" onClick={() => setActiveAccount(null)} aria-label="Close">x</button>
            </div>

            <div className="drawer-section">
              <p className="drawer-section-title">Account Leads</p>
              {activeAccount.leads.map((lead) => (
                <div key={lead.id} className="drawer-row drawer-row--stack">
                  <div>
                    <strong>{lead.fullName}</strong>
                    <div className="mono-cell">{lead.email}</div>
                  </div>
                  <div className="table-actions">
                    <span className={`account-health-pill account-health-pill--${lead.status.toLowerCase()}`}>{lead.status}</span>
                    <button
                      type="button"
                      className="table-action-link"
                      onClick={() => navigate(`/leads/${lead.id}/edit`)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

export default AccountsPage;
